// Import type configurations for the mass import tool

const PERCENT_MESSAGE = 'Use Percentage format in your spreadsheet before importing, '
  + 'or enter the value as a decimal (e.g. 0.1 for 10%).';

export const IMPORT_TYPES = {
  MATERIALS: 'materials',
  COST_CODES: 'costCodes',
  PROJECTS: 'projects',
  CUSTOMERS: 'customers',
  VENDORS: 'vendors',
  EQUIPMENT: 'equipment',
  USERS: 'users',
  UNIONS: 'unions',
  BUCKETS: 'buckets',
  BULK_USER_UPDATE: 'bulkUserUpdate',
  BULK_MATERIAL_UPDATE: 'bulkMaterialUpdate',
  BULK_EQUIPMENT_UPDATE: 'bulkEquipmentUpdate',
};

export const IMPORT_TYPE_OPTIONS = [
  {
    key: IMPORT_TYPES.MATERIALS,
    label: 'Materials',
    icon: 'materials',
    route: '/materials',
    type: 'import',
  },
  {
    key: IMPORT_TYPES.COST_CODES,
    label: 'Cost Codes',
    icon: 'table',
    route: '/costcodes/global',
    type: 'import',
  },
  {
    key: IMPORT_TYPES.PROJECTS,
    label: 'Projects',
    icon: 'project',
    route: '/projects',
    type: 'import',
  },
  {
    key: IMPORT_TYPES.CUSTOMERS,
    label: 'Customers',
    icon: 'customers',
    route: '/contacts/customers',
    type: 'import',
  },
  {
    key: IMPORT_TYPES.VENDORS,
    label: 'Vendors',
    icon: 'vendors',
    route: '/contacts/vendors',
    type: 'import',
  },
  {
    key: IMPORT_TYPES.EQUIPMENT,
    label: 'Equipment',
    icon: 'equipment',
    route: '/equipment',
    type: 'import',
  },
  {
    key: IMPORT_TYPES.USERS,
    label: 'Users',
    icon: 'user',
    route: '/users',
    type: 'import',
  },
  {
    key: IMPORT_TYPES.UNIONS,
    label: 'Unions',
    icon: 'union',
    route: '/settings/unions',
    type: 'import',
  },
  {
    key: IMPORT_TYPES.BUCKETS,
    label: 'Buckets',
    icon: 'AntDesignOutlined',
    route: '/buckets',
    type: 'import',
  },
  {
    key: IMPORT_TYPES.BULK_USER_UPDATE,
    label: 'User Update',
    icon: 'user',
    route: '/users',
    type: 'bulkUpdate',
  },
  {
    key: IMPORT_TYPES.BULK_MATERIAL_UPDATE,
    label: 'Material Update',
    icon: 'materials',
    route: '/materials',
    type: 'bulkUpdate',
  },
  {
    key: IMPORT_TYPES.BULK_EQUIPMENT_UPDATE,
    label: 'Equipment Update',
    icon: 'equipment',
    route: '/equipment',
    type: 'bulkUpdate',
  },
];

// Field configurations for each import type
// Based on backend schemas from pms-backend/schemas/schemas.js and route handlers
export const IMPORT_TYPE_CONFIGS = {
  [IMPORT_TYPES.MATERIALS]: {
    label: 'Materials',
    route: '/materials',
    headers: [
      { key: 'name', title: 'Name', required: true },
      { key: 'description', title: 'Description', required: true },
      { key: 'partNumber', title: 'Part Number', required: true },
      { key: 'division', title: 'Division', required: false },
      { key: 'costcode', title: 'Cost Code', required: false },
      { key: 'supplier', title: 'Supplier', required: false },
      { key: 'cost', title: 'Cost', required: false },
      { key: 'markup', title: 'Markup (%)', required: false, helperText: PERCENT_MESSAGE },
      { key: 'units', title: 'Units', required: false },
      { key: 'quantity', title: 'Quantity', required: false },
      { key: 'quantityThreshold', title: 'Quantity Threshold', required: false },
      { key: 'labourCost', title: 'Labour Cost', required: false },
      { key: 'labourMarkup', title: 'Labour Markup (%)', required: false, helperText: PERCENT_MESSAGE },
      { key: 'vendor', title: 'Vendor', required: false },
    ],
  },
  [IMPORT_TYPES.COST_CODES]: {
    label: 'Cost Codes',
    route: '/costcodes/global',
    headers: [
      { key: 'code', title: 'Code', required: true },
      { key: 'name', title: 'Name', required: true },
      { key: 'description', title: 'Description', required: true },
      { key: 'division', title: 'Division', required: false },
      { key: 'category', title: 'Category', required: false },
      { key: 'hourlyWage', title: 'Hourly Wage', required: false },
      { key: 'dailyWage', title: 'Daily Wage', required: false },
      { key: 'wageAdjustment', title: 'Wage Adjustment', required: false },
      { key: 'wageMultiplier', title: 'Wage Multiplier', required: false },
      { key: 'hourlyBillingRate', title: 'Hourly Billing Rate', required: false },
      { key: 'dailyBillingRate', title: 'Daily Billing Rate', required: false },
    ],
  },
  [IMPORT_TYPES.PROJECTS]: {
    label: 'Projects',
    route: '/projects',
    headers: [
      { key: 'name', title: 'Name', required: true },
      { key: 'number', title: 'Project Number', required: false },
      { key: 'address', title: 'Address', required: true },
      { key: 'division', title: 'Division', required: false },
      { key: 'customer', title: 'Customer', required: false },
      { key: 'projectType', title: 'Project Type', required: false },
      { key: 'info', title: 'Info', required: false },
      { key: 'startDate', title: 'Start Date', required: false },
      { key: 'endDate', title: 'End Date', required: false },
      { key: 'geofence', title: 'Geofence (meters)', required: false },
      { key: 'contractValue', title: 'Contract Value', required: false },
      { key: 'estimatedLabourCost', title: 'Estimated Labour Cost', required: false },
      { key: 'estimatedLabourHours', title: 'Estimated Labour Hours', required: false },
      { key: 'estimatedMaterialCost', title: 'Estimated Material Cost', required: false },
      { key: 'estimatedOverheadCost', title: 'Estimated Overhead Cost', required: false },
      { key: 'estimatedEquipmentCost', title: 'Estimated Equipment Cost', required: false },
      { key: 'labels', title: 'Labels', required: false, caseSensitiveMatch: true },
    ],
  },
  [IMPORT_TYPES.CUSTOMERS]: {
    label: 'Customers',
    route: '/contacts/customers',
    headers: [
      { key: 'name', title: 'Name', required: true },
      { key: 'division', title: 'Division', required: false },
      { key: 'officeEmail', title: 'Office Email', required: false },
      { key: 'billingEmail', title: 'Billing Email', required: false },
      { key: 'officeNumber', title: 'Office Phone Number', required: false },
      { key: 'mobileNumber', title: 'Mobile Phone Number', required: false },
      { key: 'faxNumber', title: 'Fax Number', required: false },
      { key: 'officeAddress', title: 'Office Address', required: false },
      { key: 'billingAddress', title: 'Billing Address', required: false },
      { key: 'website', title: 'Website', required: false },
      { key: 'info', title: 'Notes', required: false },
      { key: 'materialDiscount', title: 'Material Discount/Markup (%)', required: false },
    ],
  },
  [IMPORT_TYPES.VENDORS]: {
    label: 'Vendors',
    route: '/contacts/vendors',
    headers: [
      { key: 'name', title: 'Name', required: true },
      { key: 'division', title: 'Division', required: false },
      { key: 'officeEmail', title: 'Office Email', required: false },
      { key: 'billingEmail', title: 'Billing Email', required: false },
      { key: 'officeNumber', title: 'Office Phone Number', required: false },
      { key: 'mobileNumber', title: 'Mobile Phone Number', required: false },
      { key: 'faxNumber', title: 'Fax Number', required: false },
      { key: 'officeAddress', title: 'Office Address', required: false },
      { key: 'billingAddress', title: 'Billing Address', required: false },
      { key: 'website', title: 'Website', required: false },
      { key: 'info', title: 'Notes', required: false },
      { key: 'type', title: 'Type', required: true },
    ],
  },
  [IMPORT_TYPES.EQUIPMENT]: {
    label: 'Equipment',
    route: '/equipment',
    headers: [
      { key: 'name', title: 'Name', required: true },
      { key: 'division', title: 'Division', required: false },
      { key: 'status', title: 'Status', required: true },
      { key: 'code', title: 'ID', required: false },
      { key: 'equipmentType', title: 'Equipment Type', required: false },
      { key: 'costcode', title: 'Cost Code', required: false },
      { key: 'hourlyCost', title: 'Hourly Cost', required: false },
      { key: 'hourlyBillingRate', title: 'Hourly Billing Rate', required: false },
      { key: 'dailyCost', title: 'Daily Cost', required: false },
      { key: 'dailyBillingRate', title: 'Daily Billing Rate', required: false },
      { key: 'weeklyCost', title: 'Weekly Cost', required: false },
      { key: 'weeklyBillingRate', title: 'Weekly Billing Rate', required: false },
      { key: 'monthlyCost', title: 'Monthly Cost', required: false },
      { key: 'monthlyBillingRate', title: 'Monthly Billing Rate', required: false },
      { key: 'labels', title: 'Labels', required: false, caseSensitiveMatch: true },
      { key: 'vendor', title: 'Vendor', required: false },
    ],
  },
  [IMPORT_TYPES.USERS]: {
    label: 'Users',
    route: '/users',
    headers: [
      { key: 'name', title: 'Name', required: true },
      { key: 'username', title: 'Username', required: false },
      { key: 'email', title: 'Email', required: false },
      { key: 'password', title: 'Password', required: false },
      { key: 'position', title: 'Position', required: true },
      { key: 'division', title: 'Division', required: false },
      { key: 'phoneNumber', title: 'Phone Number', required: false },
      { key: 'employeeId', title: 'Employee ID', required: false },
      { key: 'wage', title: 'Wage', required: false },
      { key: 'timezone', title: 'Timezone', required: false },
      { key: 'address', title: 'Address', required: false },
    ],
  },
  [IMPORT_TYPES.UNIONS]: {
    label: 'Unions',
    route: '/settings',
    supportRollback: false,
    headers: [
      { key: 'union', title: 'Union', required: true },
      { key: 'local', title: 'Local', required: false },
      { key: 'localDescription', title: 'Local Description', required: false },
      { key: 'class', title: 'Class', required: false },
      { key: 'classDescription', title: 'Class Description', required: false },
      { key: 'classWage', title: 'Class Wage', required: false },
    ],
  },
  [IMPORT_TYPES.BULK_USER_UPDATE]: {
    label: 'Bulk User Update',
    route: '/users',
    supportRollback: false,
    resultsSuccessText: 'updated',
    headers: [
      { key: 'name', title: 'Name', required: true, caseSensitiveMatch: true },
      { key: 'employeeId', title: 'Employee ID', required: false },
      { key: 'union', title: 'Union', required: false, caseSensitiveMatch: true },
      { key: 'local', title: 'Local', required: false, caseSensitiveMatch: true },
      { key: 'unionClass', title: 'Class', required: false, caseSensitiveMatch: true },
      { key: 'wage', title: 'Wage', required: false },
      { key: 'position', title: 'Role', required: false, caseSensitiveMatch: true },
    ],
  },
  [IMPORT_TYPES.BUCKETS]: {
    label: 'Buckets',
    route: '/buckets',
    headers: [
      { key: 'name', title: 'Name', required: true },
      { key: 'description', title: 'Description', required: false },
      { key: 'status', title: 'Status', required: false },
      { key: 'customer', title: 'Customer', required: false },
      { key: 'project', title: 'Project', required: false },
      // Custom fields will be added dynamically based on selected bucket template
    ],
  },
  [IMPORT_TYPES.BULK_MATERIAL_UPDATE]: {
    label: 'Bulk Material Update',
    route: '/materials',
    supportRollback: false,
    resultsSuccessText: 'updated',
    headers: [
      { key: 'name', title: 'Name', required: true },
      { key: 'cost', title: 'Cost', required: false },
      { 
        key: 'markup', 
        title: 'Markup (%)', 
        required: false,
        helperText: PERCENT_MESSAGE,
      },
      { key: 'labourCost', title: 'Labour Cost', required: false },
      { 
        key: 'labourMarkup', 
        title: 'Labour Markup (%)', 
        required: false,
        helperText: PERCENT_MESSAGE,
      },
      { key: 'units', title: 'Units', required: false },
      { key: 'location', title: 'Location', required: false },
      { key: 'locationQuantity', title: 'Location Quantity', required: false },
    ],
  },
  [IMPORT_TYPES.BULK_EQUIPMENT_UPDATE]: {
    label: 'Bulk Equipment Update',
    route: '/equipment',
    supportRollback: false,
    resultsSuccessText: 'updated',
    headers: [
      { key: 'name', title: 'Name', required: false },
      { key: 'code', title: 'ID', required: false },
      { key: 'hourlyBillingRate', title: 'Hourly Billing Rate', required: false },
      { key: 'dailyBillingRate', title: 'Daily Billing Rate', required: false },
      { key: 'weeklyBillingRate', title: 'Weekly Billing Rate', required: false },
      { key: 'monthlyBillingRate', title: 'Monthly Billing Rate', required: false },
    ],
  },
};
